import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { MEActivityDTO } from './me-activity-dto';
import { ActivityTitle, MEMeta } from './me-meta';
import { LogEvent, PerformanceService } from 'src/app/system/utility/performance.service';

@Injectable({
  providedIn: 'root',
})
export class MEActivityService {

  
  readonly currentState$: BehaviorSubject<ActivityState> = new BehaviorSubject<ActivityState>(ActivityState.None);
  public updateScreen$: Subject<void> = new Subject<void>();
  
  public dto$ : BehaviorSubject<any> = new BehaviorSubject<any>(null);
  
  private previousState = ActivityState.None;
  currentActivity!: MEActivityDTO;
  registeredActivities: Map<string, MEActivityDTO> = new Map();
  meMeta!: MEMeta;
  currentTitle$: BehaviorSubject<ActivityTitle> = new BehaviorSubject<ActivityTitle>(new ActivityTitle(''));

  currentLoadTrigger = LoadDataTrigger.INIT;

  constructor() {}

  private buildKey(activity: string, template: string): string {
    return activity + '-' + template;
  }

  registerActivity(
    activity: string,
    dtoActivity: MEActivityDTO,
    template: string = 'standard'
  ) {
    
    let key = this.buildKey(activity, template);

    if (!this.registeredActivities.has(key)) {
      this.registeredActivities.set(activity + '-' + template, dtoActivity);
    }

    // Start the Trace
    PerformanceService.startTrace();
  }

  initState(
    activity: string,
    meta: MEMeta,
    template: string = 'standard'
  ): void {
    this.meMeta = meta;
    this.currentTitle$.next(this.meMeta.getTitles()[ActivityState.Input]);
    let key = this.buildKey(activity, template);

    if (!this.registeredActivities.has(key))
      throw new Error('Unregistered Activity');

    this.currentActivity = this.registeredActivities.get(key)!;
    this.currentState$.next(ActivityState.None);
    this.previousState = ActivityState.None;

    // Stop the trace
    PerformanceService.stopTrace();
  }

  nextState(): void {

    this.previousState = this.currentState$.value;
    if (this.currentState$.value == ActivityState.None) {
      this.currentState$.next(ActivityState.Input);
      this.currentTitle$.next(this.meMeta.getTitles()[ActivityState.Input]);
    } else if (this.currentState$.value == ActivityState.Input) {
      this.currentState$.next(ActivityState.Review);
      this.currentTitle$.next(this.meMeta.getTitles()[ActivityState.Review]);
    } else if (this.currentState$.value == ActivityState.Review) {
      this.currentState$.next(ActivityState.Summary);
      this.currentTitle$.next(this.meMeta.getTitles()[ActivityState.Summary]);
    }

    // Log Event
    let eventParams: LogEvent = {
      currentState:  this.currentState$.value,
      prevState: this.previousState,
    }
    PerformanceService.logEvent(eventParams);
  }

  prevState(): void {
    this.previousState = this.currentState$.value;
    if (this.currentState$.value == ActivityState.Review) {
      this.currentState$.next(ActivityState.Input);
      this.currentTitle$.next(this.meMeta.getTitles()[ActivityState.Input]);
    }
  }

  reset(): void {
    this.previousState = ActivityState.None;
    this.currentState$.next(ActivityState.None);
    this.currentTitle$.next(new ActivityTitle(''));
    this.dto$.next(null);
  }

  getPreviousState() : ActivityState{
    return this.previousState;
  }

  updateActivityTitle(state: ActivityState, condition: boolean, title: ActivityTitle) {
    if (condition && state !== ActivityState.None) {
      this.meMeta.updateTitle(state, title);
      this.currentTitle$.next(this.meMeta.getTitles()[state]);
    }
  }

  /**
   * Behaviour Subject for component communication between Header and Form Body
   */
  public onBackClick$ : Subject<void> = new Subject<void>();
  public onBackClick() {
    this.onBackClick$.next();
  }

  public setActivityDTO(dto: any, trigger: LoadDataTrigger = LoadDataTrigger.INIT) {
    this.currentLoadTrigger = trigger;
    this.dto$.next(dto);
  }
}

export enum ActivityState {
  None = 'None',
  Input = 'Input',
  Review = 'Review',
  Summary = 'Summary',
}

export enum LoadDataTrigger {
  INIT = 'INIT',
  TEMPLATE_CHANGE = 'TEMPLATE_CHANGE',
  EDIT_LOAD = 'EDIT_LOAD',
}